import React, { useEffect, useState } from 'react';
import { Calendar, Clock, MapPin, Users, Video } from 'lucide-react';
import { api } from '../api';

export default function ShootDaysPanel({ project, days: daysProp }) {
  const [fetchedDays, setFetchedDays] = useState([]);
  const [loading, setLoading] = useState(daysProp === undefined);
  // Parent may hoist the fetch and pass `days` down (preferred, keeps this
  // panel and the day-filter pills in sync). Fall back to self-fetch when
  // no prop is supplied.
  const days = daysProp !== undefined ? daysProp : fetchedDays;

  useEffect(() => {
    if (daysProp !== undefined) return; // parent-provided, don't refetch
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const ds = await api.listProjectDays(project.id);
        if (!cancelled) setFetchedDays(ds || []);
      } catch {
        if (!cancelled) setFetchedDays([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.id, daysProp]);

  // Only render the panel for PM-linked projects
  if (!project.externalProjectId) return null;

  return (
    <div className="surface rounded-lg p-4 mb-5" data-testid="shoot-days-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-amber-400" />
          <span className="text-sm font-semibold text-white">Shoot Days</span>
          {days.length > 0 && (
            <span className="text-[11px] text-neutral-500">
              {days.length} day{days.length === 1 ? '' : 's'} from Project Manager
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-[11px] text-neutral-500">Loading…</div>
      ) : days.length === 0 ? (
        <div className="text-[11px] text-neutral-500 leading-relaxed">
          No call-sheet days have been pulled from Project Manager yet.{' '}
          Once the PM app exposes per-day data for this project, days will
          appear here automatically.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {days.map((d) => {
            const label = d.dayLabel?.trim() || `Day ${d.dayNumber}`;
            return (
              <div
                key={d.id}
                className="rounded-md border border-neutral-900 bg-neutral-950 px-3 py-2"
                data-testid={`shoot-day-${d.dayNumber}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-amber-400 font-medium">
                    {label}
                  </div>
                  {d.shootDate && (
                    <div className="text-[11px] text-neutral-300 font-mono">{d.shootDate}</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-400">
                  {d.generalCall && (
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} className="text-neutral-600" />
                      Call {d.generalCall}
                    </span>
                  )}
                  {d.estWrap && (
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} className="text-neutral-600" />
                      Wrap {d.estWrap}
                    </span>
                  )}
                  {d.locationCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={11} className="text-neutral-600" />
                      {d.locationCount} loc
                    </span>
                  )}
                  {d.crewCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users size={11} className="text-neutral-600" />
                      {d.crewCount} crew
                    </span>
                  )}
                  {d.talentCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Video size={11} className="text-neutral-600" />
                      {d.talentCount} talent
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
